import { useState, useEffect, useRef, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import axios from 'axios';
import {
  Bot, Send, Calculator, Upload, Trash2, Fuel,
  MapPin, Package, Truck, Clock, DollarSign,
  MessageSquare, Plus, ChevronDown, FileSpreadsheet,
  Loader2, ArrowRight, X, AlertTriangle, CheckCircle,
  Brain, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_TYPES = [
  { value: 'dry_van', label: 'Dry Van (53\')' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'reefer', label: 'Reefer (Temperature controlee)' },
  { value: 'step_deck', label: 'Step Deck' },
  { value: 'tanker', label: 'Citerne' },
  { value: 'other', label: 'Autre' },
];

const LOAD_TYPES = [
  { value: 'TL', label: 'TL (Truckload)' },
  { value: 'LTL', label: 'LTL (Less Than Truckload)' },
  { value: 'partial', label: 'Partiel' },
];

const inputClass = "w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm";
const labelClass = "block text-xs font-medium text-slate-400 mb-1.5";

// ==================== QUICK FORM ====================

const QuickForm = ({ token, fuelPrice, onCorrection }) => {
  const [form, setForm] = useState({
    origin: '', destination: '', load_type: 'TL', equipment: 'dry_van',
    weight: '', pallets: '', distance_km: '', notes: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctedPrice, setCorrectedPrice] = useState('');
  const [corrNote, setCorrNote] = useState('');

  const submitCorrection = async () => {
    if (!correctedPrice) return;
    try {
      await axios.post(`${API}/api/rate-agent/corrections`, {
        origin: form.origin, destination: form.destination,
        load_type: form.load_type, equipment: form.equipment,
        original_price: parsedResult?.total || 0,
        corrected_price: parseFloat(correctedPrice),
        note: corrNote,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Correction sauvegardee - l\'IA apprendra de ce prix');
      setShowCorrection(false);
      setCorrectedPrice('');
      setCorrNote('');
      if (onCorrection) onCorrection();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.origin || !form.destination) {
      toast.error('Origine et destination requises');
      return;
    }
    try {
      setLoading(true);
      setResult(null);
      const res = await axios.post(`${API}/api/rate-agent/calculate`, form, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setResult(res.data.result);
    } catch (err) {
      toast.error('Erreur de calcul');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parsedResult = (() => {
    if (!result) return null;
    try {
      const cleaned = result.replace(/```json?\n?/g, '').replace(/```/g, '').trim();
      return JSON.parse(cleaned);
    } catch {
      return null;
    }
  })();

  return (
    <div className="space-y-4" data-testid="quick-form">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}><MapPin size={12} className="inline mr-1" />Origine</label>
          <input className={inputClass} placeholder="Ex: Montreal, QC" value={form.origin}
            onChange={e => setForm({...form, origin: e.target.value})} required data-testid="form-origin" />
        </div>
        <div>
          <label className={labelClass}><MapPin size={12} className="inline mr-1" />Destination</label>
          <input className={inputClass} placeholder="Ex: Quebec, QC" value={form.destination}
            onChange={e => setForm({...form, destination: e.target.value})} required data-testid="form-destination" />
        </div>
        <div>
          <label className={labelClass}><Truck size={12} className="inline mr-1" />Type de charge</label>
          <select className={inputClass} value={form.load_type}
            onChange={e => setForm({...form, load_type: e.target.value})} data-testid="form-load-type">
            {LOAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}><Package size={12} className="inline mr-1" />Equipement</label>
          <select className={inputClass} value={form.equipment}
            onChange={e => setForm({...form, equipment: e.target.value})} data-testid="form-equipment">
            {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Poids (lbs)</label>
          <input className={inputClass} placeholder="Ex: 40000" value={form.weight} type="number"
            onChange={e => setForm({...form, weight: e.target.value})} data-testid="form-weight" />
        </div>
        <div>
          <label className={labelClass}>Palettes</label>
          <input className={inputClass} placeholder="Ex: 24" value={form.pallets} type="number"
            onChange={e => setForm({...form, pallets: e.target.value})} data-testid="form-pallets" />
        </div>
        <div>
          <label className={labelClass}>Distance (km)</label>
          <input className={inputClass} placeholder="Optionnel - sera estime" value={form.distance_km} type="number"
            onChange={e => setForm({...form, distance_km: e.target.value})} data-testid="form-distance" />
        </div>
        <div className="md:col-span-2">
          <label className={labelClass}>Notes</label>
          <input className={inputClass} placeholder="Details additionnels (hazmats, surdimensionne, etc.)" value={form.notes}
            onChange={e => setForm({...form, notes: e.target.value})} data-testid="form-notes" />
        </div>

        <div className="flex items-end">
          <button type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-black font-bold px-4 py-2.5 rounded-lg transition-colors"
            data-testid="calculate-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Calculator size={18} />}
            {loading ? 'Calcul en cours...' : 'Calculer le prix'}
          </button>
        </div>
      </form>

      {/* Fuel price indicator */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Fuel size={12} />
        <span>Diesel: {fuelPrice?.toFixed(2) || '1.85'}$/L</span>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5" data-testid="calc-result">
          {parsedResult ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                  <Calculator size={16} /> Estimation tarifaire
                </h3>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  parsedResult.confiance === 'haute' ? 'bg-emerald-500/20 text-emerald-400' :
                  parsedResult.confiance === 'moyenne' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  Confiance: {parsedResult.confiance || '?'}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Tarif de base</p>
                  <p className="text-lg font-bold text-white">{parsedResult.tarif_base?.toLocaleString()}$</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">FSC ({parsedResult.fsc_pct}%)</p>
                  <p className="text-lg font-bold text-amber-400">{parsedResult.fsc_montant?.toLocaleString()}$</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Frais add.</p>
                  <p className="text-lg font-bold text-slate-300">{parsedResult.frais_additionnels?.toLocaleString()}$</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-400">TOTAL</p>
                  <p className="text-xl font-black text-amber-400">{parsedResult.total?.toLocaleString()}$</p>
                </div>
              </div>

              {parsedResult.details && (
                <p className="text-sm text-slate-400 leading-relaxed">{parsedResult.details}</p>
              )}

              {/* Quick correction */}
              {!showCorrection ? (
                <button onClick={() => { setShowCorrection(true); setCorrectedPrice(parsedResult.total?.toString() || ''); }}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-400 transition-colors mt-1"
                  data-testid="quick-correct-btn">
                  <Brain size={12} /> Ce prix n'est pas exact? Corriger pour entrainer l'IA
                </button>
              ) : (
                <div className="flex items-center gap-2 mt-2 bg-slate-800/60 rounded-lg p-3">
                  <input type="number" value={correctedPrice} onChange={e => setCorrectedPrice(e.target.value)}
                    className="w-28 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                    placeholder="Prix $" data-testid="quick-corr-price" />
                  <input value={corrNote} onChange={e => setCorrNote(e.target.value)}
                    className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                    placeholder="Note (optionnel)" />
                  <button onClick={submitCorrection}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded text-xs transition-colors"
                    data-testid="quick-corr-save">Sauvegarder</button>
                  <button onClick={() => setShowCorrection(false)} className="text-slate-500 hover:text-white text-xs">Annuler</button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Bot size={16} /> Reponse IA
              </h3>
              <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{result}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CHAT INTERFACE ====================

const ChatInterface = ({ token, fuelPrice }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const messagesEndRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/rate-agent/chat-sessions`, { headers });
      setSessions(res.data.sessions || []);
    } catch (err) { console.error(err); }
    finally { setLoadingSessions(false); }
  }, [token]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/api/rate-agent/chat/${sessionId}`, { headers });
      setMessages(res.data.messages || []);
      setActiveSession(sessionId);
    } catch (err) { console.error(err); }
  };

  const newSession = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMsg = input.trim();
    setInput('');
    setSending(true);

    const sessionId = activeSession || `session-${Date.now()}`;
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);

    try {
      const res = await axios.post(`${API}/api/rate-agent/chat`, {
        session_id: sessionId,
        message: userMsg,
      }, { headers, timeout: 60000 });

      setActiveSession(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      fetchSessions();
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur: impossible de traiter votre demande.' }]);
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/api/rate-agent/chat/${sessionId}`, { headers });
      if (activeSession === sessionId) newSession();
      fetchSessions();
    } catch (err) { console.error(err); }
  };

  const formatMessage = (content) => {
    try {
      const cleaned = content.replace(/```json?\n?/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(cleaned);
      if (parsed.total !== undefined) {
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">Base:</span> <span className="text-white font-bold">{parsed.tarif_base?.toLocaleString()}$</span></div>
              <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">FSC:</span> <span className="text-amber-400 font-bold">{parsed.fsc_montant?.toLocaleString()}$</span></div>
              <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">Frais:</span> <span className="text-slate-300 font-bold">{parsed.frais_additionnels?.toLocaleString()}$</span></div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2"><span className="text-amber-400">Total:</span> <span className="text-amber-400 font-black">{parsed.total?.toLocaleString()}$</span></div>
            </div>
            {parsed.details && <p className="text-xs text-slate-400">{parsed.details}</p>}
          </div>
        );
      }
    } catch {}
    return <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>;
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-240px)] min-h-[500px]" data-testid="chat-interface">
      {/* Sidebar */}
      <div className="w-56 shrink-0 bg-slate-900/80 border border-slate-800 rounded-xl flex flex-col overflow-hidden">
        <button onClick={newSession}
          className="m-2 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold px-3 py-2 rounded-lg text-sm transition-colors"
          data-testid="new-chat-btn">
          <Plus size={14} /> Nouvelle conversation
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {loadingSessions ? (
            <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-slate-500" /></div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-slate-600 text-center py-4">Aucune conversation</p>
          ) : sessions.map(s => (
            <div key={s.session_id}
              className={`group flex items-center gap-1 px-2 py-2 rounded-lg cursor-pointer text-xs transition-colors ${
                activeSession === s.session_id ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
              onClick={() => loadSession(s.session_id)}>
              <MessageSquare size={12} className="shrink-0" />
              <span className="flex-1 truncate">{s.preview}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-opacity">
                <Trash2 size={10} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-slate-900/80 border border-slate-800 rounded-xl flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-3">
                <Bot size={28} className="text-amber-500" />
              </div>
              <h3 className="text-white font-bold mb-1">Rate-Agent IA</h3>
              <p className="text-slate-500 text-sm max-w-md">
                Demandez un prix de transport en langage naturel. Exemples:
              </p>
              <div className="mt-3 space-y-1.5">
                {[
                  "Combien pour un TL Montreal a Quebec en dry van?",
                  "Prix LTL 5000 lbs de Montreal a Toronto?",
                  "Flatbed surdimensionne Trois-Rivieres a Gatineau?"
                ].map((q, i) => (
                  <button key={i} onClick={() => { setInput(q); }}
                    className="block w-full text-left text-xs text-slate-400 hover:text-amber-400 bg-slate-800/50 hover:bg-slate-800 px-3 py-2 rounded-lg transition-colors"
                    data-testid={`suggestion-${i}`}>
                    <ArrowRight size={10} className="inline mr-1.5" />{q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-amber-500/15 border border-amber-500/20 text-white'
                  : 'bg-slate-800/80 border border-slate-700 text-slate-300'
              }`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Bot size={12} className="text-amber-500" />
                    <span className="text-[10px] text-amber-500 font-semibold">Rate-Agent</span>
                  </div>
                )}
                {formatMessage(msg.content)}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Analyse en cours...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-3 border-t border-slate-800">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Decrivez votre besoin de transport..."
              className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={sending}
              data-testid="chat-input"
            />
            <button type="submit" disabled={sending || !input.trim()}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-black font-bold rounded-xl transition-colors"
              data-testid="chat-send-btn">
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== SETTINGS PANEL ====================

const SettingsPanel = ({ token, fuelPrice, setFuelPrice, onGridChange }) => {
  const [priceInput, setPriceInput] = useState(fuelPrice?.toString() || '1.85');
  const [uploading, setUploading] = useState(false);
  const [grid, setGrid] = useState(null);
  const [loadingGrid, setLoadingGrid] = useState(true);
  // Corrections
  const [corrections, setCorrections] = useState([]);
  const [showCorrForm, setShowCorrForm] = useState(false);
  const [corrForm, setCorrForm] = useState({ origin: '', destination: '', load_type: 'TL', equipment: 'dry_van', original_price: '', corrected_price: '', note: '' });
  // Rules
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [gridRes, corrRes, rulesRes] = await Promise.all([
          axios.get(`${API}/api/rate-agent/rate-grid`, { headers }),
          axios.get(`${API}/api/rate-agent/corrections`, { headers }),
          axios.get(`${API}/api/rate-agent/rules`, { headers }),
        ]);
        setGrid(gridRes.data.has_grid ? gridRes.data.grid : null);
        setCorrections(corrRes.data.corrections || []);
        setRules(rulesRes.data.rules || []);
      } catch (err) { console.error(err); }
      finally { setLoadingGrid(false); }
    };
    fetchAll();
  }, [token]);

  const saveFuelPrice = async () => {
    try {
      await axios.put(`${API}/api/rate-agent/fuel-price`, { diesel_price: parseFloat(priceInput) }, { headers });
      setFuelPrice(parseFloat(priceInput));
      toast.success('Prix du diesel mis a jour');
    } catch (err) { toast.error('Erreur'); }
  };

  const uploadGrid = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/api/rate-agent/rate-grid`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      const data = res.data;
      let msg = `Grille importee: ${data.row_count} lignes`;
      if (data.total_pages > 1) {
        msg += ` (${data.processed_pages}/${data.total_pages} pages)`;
      }
      if (data.failed_pages?.length > 0) {
        msg += ` — ${data.failed_pages.length} page(s) echouee(s)`;
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      setGrid({ row_count: data.row_count, columns: data.columns, filename: file.name });
      if (onGridChange) onGridChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import fichier');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteGrid = async () => {
    try {
      await axios.delete(`${API}/api/rate-agent/rate-grid`, { headers });
      setGrid(null);
      toast.success('Grille supprimee');
      if (onGridChange) onGridChange();
    } catch (err) { toast.error('Erreur'); }
  };

  const saveCorrection = async () => {
    if (!corrForm.origin || !corrForm.destination || !corrForm.corrected_price) {
      toast.error('Origine, destination et prix corrige requis');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/rate-agent/corrections`, {
        ...corrForm,
        original_price: parseFloat(corrForm.original_price) || 0,
        corrected_price: parseFloat(corrForm.corrected_price),
      }, { headers });
      setCorrections(prev => [res.data.correction, ...prev]);
      setCorrForm({ origin: '', destination: '', load_type: 'TL', equipment: 'dry_van', original_price: '', corrected_price: '', note: '' });
      setShowCorrForm(false);
      toast.success('Correction sauvegardee - l\'IA en tiendra compte');
    } catch (err) { toast.error('Erreur'); }
  };

  const deleteCorrection = async (id) => {
    try {
      await axios.delete(`${API}/api/rate-agent/corrections/${id}`, { headers });
      setCorrections(prev => prev.filter(c => c.id !== id));
      toast.success('Correction supprimee');
    } catch (err) { toast.error('Erreur'); }
  };

  const saveRule = async () => {
    if (!newRule.trim()) return;
    try {
      const res = await axios.post(`${API}/api/rate-agent/rules`, { rule: newRule.trim() }, { headers });
      setRules(prev => [res.data.rule, ...prev]);
      setNewRule('');
      toast.success('Regle ajoutee');
    } catch (err) { toast.error('Erreur'); }
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/api/rate-agent/rules/${id}`, { headers });
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Regle supprimee');
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="settings-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Fuel Price */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
            <Fuel size={16} className="text-amber-500" /> Prix du diesel
          </h3>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className={labelClass}>Prix actuel ($/L)</label>
              <input type="number" step="0.01" value={priceInput}
                onChange={e => setPriceInput(e.target.value)}
                className={inputClass} data-testid="fuel-price-input" />
            </div>
            <div className="flex items-end">
              <button onClick={saveFuelPrice}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-sm transition-colors"
                data-testid="save-fuel-btn">
                Sauvegarder
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Ce prix sera utilise pour calculer le Fuel Surcharge (FSC) automatiquement.
          </p>
        </div>

        {/* Rate Grid */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
            <FileSpreadsheet size={16} className="text-amber-500" /> Grille tarifaire
          </h3>

          {loadingGrid ? (
            <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-slate-500" /></div>
          ) : grid ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-400" />
                  <div>
                    <p className="text-sm text-white font-medium">{grid.filename || 'Grille active'}</p>
                    <p className="text-[11px] text-slate-400">{grid.row_count} lignes | {grid.columns?.length} colonnes</p>
                  </div>
                </div>
                <button onClick={deleteGrid}
                  className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                  data-testid="delete-grid-btn">
                  <Trash2 size={14} />
                </button>
              </div>
              {grid.columns && (
                <p className="text-[11px] text-slate-500">
                  Colonnes: {grid.columns.join(', ')}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <AlertTriangle size={14} className="text-amber-500" />
                <span>Aucune grille tarifaire. L'IA utilisera les tarifs du marche.</span>
              </div>
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-700 hover:border-amber-500/50 rounded-lg cursor-pointer transition-colors group">
                <input type="file" accept=".csv,.pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={uploadGrid} disabled={uploading} data-testid="upload-grid-input" />
                {uploading ? <Loader2 size={16} className="animate-spin text-amber-500" /> : <Upload size={16} className="text-slate-400 group-hover:text-amber-500" />}
                <span className="text-sm text-slate-400 group-hover:text-amber-400 font-medium">
                  {uploading ? 'Extraction IA en cours (peut prendre quelques minutes)...' : 'Importer un fichier (CSV, PDF, Image)'}
                </span>
              </label>
            </div>
          )}

          <p className="text-[11px] text-slate-500 mt-3">
            Formats acceptes: CSV, PDF ou image (scan). L'IA extrait automatiquement les tarifs et s'ameliore avec vos corrections.
          </p>
        </div>
      </div>

      {/* Business Rules */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
          <Zap size={16} className="text-amber-500" /> Regles metier
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Definissez vos regles de tarification. L'IA les appliquera automatiquement a tous les calculs.
        </p>

        <div className="flex gap-2 mb-4">
          <input className={inputClass} placeholder='Ex: "Minimum 500$ par charge", "Clients reguliers -10%", "Pas de LTL sous 200$"'
            value={newRule} onChange={e => setNewRule(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveRule()}
            data-testid="rule-input" />
          <button onClick={saveRule} disabled={!newRule.trim()}
            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-black font-bold rounded-lg text-sm transition-colors whitespace-nowrap"
            data-testid="add-rule-btn">
            <Plus size={14} className="inline mr-1" /> Ajouter
          </button>
        </div>

        {rules.length > 0 ? (
          <div className="space-y-2">
            {rules.map(r => (
              <div key={r.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 group">
                <div className="flex items-center gap-2">
                  <Zap size={12} className="text-amber-500 shrink-0" />
                  <span className="text-sm text-slate-300">{r.rule}</span>
                </div>
                <button onClick={() => deleteRule(r.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 text-slate-500 transition-opacity">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-600 text-center py-2">Aucune regle definie</p>
        )}
      </div>

      {/* Corrections / Learning */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Brain size={16} className="text-amber-500" /> Apprentissage (Corrections)
          </h3>
          <button onClick={() => setShowCorrForm(!showCorrForm)}
            className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"
            data-testid="toggle-correction-form">
            <Plus size={12} /> Ajouter une correction
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Corrigez un prix et l'IA l'utilisera comme reference pour les futurs calculs similaires. Plus vous corrigez, plus l'IA devient precise.
        </p>

        {showCorrForm && (
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 mb-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className={labelClass}>Origine</label>
                <input className={inputClass} placeholder="Montreal" value={corrForm.origin}
                  onChange={e => setCorrForm({...corrForm, origin: e.target.value})} data-testid="corr-origin" />
              </div>
              <div>
                <label className={labelClass}>Destination</label>
                <input className={inputClass} placeholder="Quebec" value={corrForm.destination}
                  onChange={e => setCorrForm({...corrForm, destination: e.target.value})} data-testid="corr-destination" />
              </div>
              <div>
                <label className={labelClass}>Type</label>
                <select className={inputClass} value={corrForm.load_type}
                  onChange={e => setCorrForm({...corrForm, load_type: e.target.value})}>
                  {LOAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Equipement</label>
                <select className={inputClass} value={corrForm.equipment}
                  onChange={e => setCorrForm({...corrForm, equipment: e.target.value})}>
                  {EQUIPMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Prix original ($)</label>
                <input type="number" className={inputClass} placeholder="1200" value={corrForm.original_price}
                  onChange={e => setCorrForm({...corrForm, original_price: e.target.value})} />
              </div>
              <div>
                <label className={labelClass}>Prix corrige ($)</label>
                <input type="number" className={inputClass} placeholder="1400" value={corrForm.corrected_price}
                  onChange={e => setCorrForm({...corrForm, corrected_price: e.target.value})} data-testid="corr-price" />
              </div>
              <div>
                <label className={labelClass}>Note</label>
                <input className={inputClass} placeholder="Ex: client regulier, route difficile..." value={corrForm.note}
                  onChange={e => setCorrForm({...corrForm, note: e.target.value})} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCorrForm(false)} className="px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors">Annuler</button>
              <button onClick={saveCorrection}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg text-sm transition-colors"
                data-testid="save-correction-btn">
                Sauvegarder
              </button>
            </div>
          </div>
        )}

        {corrections.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {corrections.map(c => (
              <div key={c.id} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2 group">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Brain size={12} className="text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white font-medium">{c.origin} → {c.destination}</span>
                    <span className="text-xs text-slate-500 ml-2">{c.load_type} {c.equipment}</span>
                    {c.note && <span className="text-xs text-slate-400 ml-2">({c.note})</span>}
                  </div>
                  <div className="shrink-0 text-right">
                    {c.original_price > 0 && <span className="text-xs text-red-400 line-through mr-2">{c.original_price}$</span>}
                    <span className="text-sm text-emerald-400 font-bold">{c.corrected_price}$</span>
                  </div>
                </div>
                <button onClick={() => deleteCorrection(c.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 text-slate-500 transition-opacity ml-2">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-600 text-center py-2">Aucune correction. Utilisez le calculateur et corrigez les prix pour entrainer l'IA.</p>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const TABS = [
  { id: 'form', label: 'Calcul rapide', icon: Calculator },
  { id: 'chat', label: 'Chat IA', icon: MessageSquare },
  { id: 'settings', label: 'Configuration', icon: Fuel },
];

export default function RateAgentPage() {
  const { token, user } = useAdminAuth();
  const [tab, setTab] = useState('form');
  const [fuelPrice, setFuelPrice] = useState(1.85);

  useEffect(() => {
    const fetchFuel = async () => {
      try {
        const res = await axios.get(`${API}/api/rate-agent/fuel-price`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFuelPrice(res.data.diesel_price);
      } catch (err) { console.error(err); }
    };
    if (token) fetchFuel();
  }, [token]);

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertTriangle size={40} className="text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Acces restreint</h2>
          <p className="text-slate-400 text-sm">Cette fonctionnalite est reservee aux super administrateurs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="rate-agent-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Bot size={22} className="text-amber-500" />
            </div>
            Rate-Agent IA
          </h1>
          <p className="text-sm text-slate-400 mt-1">Tarification intelligente par IA</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-900/60 px-3 py-1.5 rounded-lg border border-slate-800">
          <Fuel size={12} className="text-amber-500" />
          Diesel: {fuelPrice?.toFixed(2)}$/L
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/60 p-1 rounded-xl border border-slate-800 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id ? 'bg-slate-800 text-white shadow' : 'text-slate-400 hover:text-slate-300'
            }`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'form' && <QuickForm token={token} fuelPrice={fuelPrice} onCorrection={() => {}} />}
      {tab === 'chat' && <ChatInterface token={token} fuelPrice={fuelPrice} />}
      {tab === 'settings' && <SettingsPanel token={token} fuelPrice={fuelPrice} setFuelPrice={setFuelPrice} />}
    </div>
  );
}
